import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Container, Divider, Stack, Typography, Link } from '@mui/material';
import { constants, ROUTES } from '../constants';
import { useNavigate } from 'react-router-dom';
import { ConfigContext, useSettingsData, usePageImage, useHeaderImage, useUserExperienceConfig } from '../components/ConfigContext';
import { AuthorizationAPI } from '../apis';
import { handleErrorModal } from '../components/Toast';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { decodeAccessToken } from '../utils/auth';
import { normalizeEmail, getSelectedLanguageFromCookie } from '../utils/utilities';
import { useApplicationsList } from '../components/ApplicationContext';
import { usePoliciesData } from '../components/PoliciesContext';
import { useTranslation } from 'react-i18next';
const sanitizeUrl = (url) => {
    if (!url)
        return '';
    const lower = url.toLowerCase().trim();
    // Intentional XSS guard: block javascript:, data:, and vbscript: pseudo-protocols
    if (lower.startsWith('javascript:') || // NOSONAR typescript:S5334
        lower.startsWith('data:') ||
        lower.startsWith('vbscript:'))
        return '';
    return url;
};
const Home = () => {
    const { i18n } = useTranslation();
    const router = useNavigate();
    const [isLogin, setIsLogin] = useState(false);
    const loginURL = process.env.REACT_APP_API_URL + '/authentication/loginRedirect';
    const { configState, setConfigState } = useContext(ConfigContext);
    const [loader, setLoader] = useState(false);
    const [firstProduct, setFirstProduct] = useState(null);
    const getApplications = useApplicationsList();
    const tokenFetchedRef = useRef(false);
    // Cached data hooks
    const getCachedSettings = useSettingsData();
    const getCachedPageImage = usePageImage();
    const getCachedHeaderImage = useHeaderImage();
    const getCachedPolicies = usePoliciesData();
    const [privacyUrl, setPrivacyUrl] = useState('');
    const [cookieUrl, setCookieUrl] = useState('');
    const [noSessionDetected, setNoSessionDetected] = useState(false);
    const [autoRedirectToLogin, setAutoRedirectToLogin] = useState(null);
    const getUxConfig = useUserExperienceConfig();
    // Load applications only after getting the token (when user is authenticated)
    const loadProducts = async () => {
        try {
            const apps = await getApplications();
            const applications = apps;
            // Filter out RBAC application and get products with productName
            const products = applications.filter((app) => app?.details?.productName && app.name !== 'RBAC');
            if (products.length > 0) {
                setFirstProduct(products[0]);
            }
        }
        catch (error) {
            console.error('Error loading applications:', error);
            // Fallback: if products can't be loaded, redirect to /apps or show error
            setFirstProduct(null);
        }
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                await Promise.all([getSettings(), getPageImage(), getHeaderImage()]);
                setClientColorInCookie();
            }
            catch (error) {
                console.error('Failed to load initial settings:', error);
            }
            try {
                const uxConfig = await getUxConfig();
                setPrivacyUrl(uxConfig['RBAC_UI_PRIVACY_URL'] ??
                    process.env.REACT_APP_PRIVACY_URL ??
                    '');
                setCookieUrl(uxConfig['RBAC_UI_COOKIE_URL'] ??
                    process.env.REACT_APP_COOKIE_URL ??
                    '');
                setAutoRedirectToLogin(uxConfig['RBAC_UI_AUTO_REDIRECT_TO_LOGIN'] === true);
            }
            catch (error) {
                console.error('Failed to load UX config:', error);
                setPrivacyUrl(process.env.REACT_APP_PRIVACY_URL ?? '');
                setCookieUrl(process.env.REACT_APP_COOKIE_URL ?? '');
                setAutoRedirectToLogin(false);
            }
        };
        fetchData();
    }, [getUxConfig]);
    const setClientColorInCookie = () => {
        const themeColor = {
            color: configState?.settings?.button_color || '#26890D'
        };
        Cookies.set('theme', JSON.stringify(themeColor), {
            secure: true,
            sameSite: 'Strict'
        });
    };
    const getSettings = async () => {
        const response = await getCachedSettings();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                ...response
            }
        }));
    };
    const getPageImage = async () => {
        const response = await getCachedPageImage();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                page_image: response
            }
        }));
    };
    const getHeaderImage = async () => {
        const response = await getCachedHeaderImage();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                header_logo_image: response
            }
        }));
    };
    useEffect(() => {
        if (isLogin) {
            globalThis.window.dispatchEvent(new Event('userAvailable'));
            if (JSON.parse(process.env.REACT_APP_SHOW_PRODUCT_SELECTION_PAGE ?? 'false')) {
                // Redirect to product selection page
                router(ROUTES.APPS);
            }
            else {
                // Redirect to first product from database
                let temp = structuredClone(configState);
                temp.selectedProduct = firstProduct?.details?.productName ?? '';
                setConfigState(temp);
                Cookies.set('Product Selected', firstProduct?.details?.productName ?? '', {
                    secure: true,
                    sameSite: 'Strict'
                });
                if (firstProduct) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { image_base64, ...detailsWithoutImage } = firstProduct.details || {};
                    const appToStore = {
                        ...firstProduct,
                        details: detailsWithoutImage
                    };
                    sessionStorage.setItem('current_application', JSON.stringify(appToStore));
                }
                router(`/${firstProduct?.details?.productName ?? ''}`);
            }
        }
        else if (!tokenFetchedRef.current) {
            tokenFetchedRef.current = true;
            getToken();
        }
    }, [isLogin]);
    useEffect(() => {
        if (!noSessionDetected)
            return;
        if (autoRedirectToLogin === null)
            return;
        if (autoRedirectToLogin) {
            globalThis.window?.location.replace(loginURL);
        }
        else {
            setLoader(false);
        }
    }, [noSessionDetected, autoRedirectToLogin, loginURL]);
    async function getToken() {
        setLoader(true);
        await AuthorizationAPI.getToken()
            .then(async (result) => {
            if (result?.accessToken) {
                Cookies.set('access_token', result.accessToken, {
                    secure: true,
                    sameSite: 'Strict'
                });
                Cookies.set('refresh_token', result.refreshToken, {
                    secure: true,
                    sameSite: 'Strict'
                });
                Cookies.set('ui_trace_id', uuidv4().replaceAll('-', ''), {
                    secure: true,
                    sameSite: 'Strict'
                });
                // Load products after authentication
                await loadProducts();
                getUser();
            }
            else {
                setNoSessionDetected(true);
            }
        })
            .catch(() => {
            setNoSessionDetected(true);
        });
    }
    const getUser = async () => {
        setLoader(true);
        if (!Cookies.get('access_token')) {
            setLoader(false);
            handleErrorModal(true, constants.home_error_access_token_not_found);
            return;
        }
        const userName = normalizeEmail(decodeAccessToken(Cookies.get('access_token')));
        if (!userName) {
            setLoader(false);
            handleErrorModal(true, constants.home_error_user_name_not_found);
            return;
        }
        await AuthorizationAPI.getUser(userName)
            .then((response) => {
            const user = response.resource.find((item) => item.username === userName);
            Cookies.set('user', JSON.stringify(user), {
                secure: true,
                sameSite: 'Strict'
            });
            // Sync language after cookie is set
            const userLanguage = getSelectedLanguageFromCookie();
            if (userLanguage && i18n.language !== userLanguage) {
                i18n.changeLanguage(userLanguage);
            }
            setConfigState({
                ...configState,
                selectedProduct: firstProduct?.details?.productName ?? ''
            });
            getPolicy();
        })
            .catch(() => {
            setLoader(false);
        });
    };
    const RedirectToLogin = () => {
        globalThis.window?.location.replace(loginURL);
    };
    const getPolicy = async () => {
        setLoader(true);
        try {
            await getCachedPolicies();
        }
        catch (error) {
            console.error('Failed to load policies:', error);
        }
        setIsLogin(true);
        setLoader(false);
    };
    const getCookiePrivacyButtons = (isNewDesign = false, textColor = '#FFFFFF') => {
        return (_jsxs(Stack, { direction: "row", spacing: 2.5, sx: { mt: 3, alignItems: 'center' }, children: [_jsx(Link, { href: sanitizeUrl(privacyUrl) || undefined, target: "_blank", rel: "noopener noreferrer", underline: "none", sx: {
                        color: textColor,
                        ...(isNewDesign && {
                            fontFamily: 'Open Sans',
                            fontSize: '16px',
                            fontWeight: 400,
                            textTransform: 'none',
                            mr: 3
                        })
                    }, children: constants.privacy }), _jsx(Divider, { orientation: "vertical", sx: {
                        border: `1px solid ${textColor}`,
                        height: '20px',
                        mt: '7px !important'
                    }, flexItem: true }), _jsx(Link, { href: sanitizeUrl(cookieUrl) || undefined, target: "_blank", rel: "noopener noreferrer", underline: "none", sx: {
                        color: textColor,
                        ...(isNewDesign && {
                            fontFamily: 'Open Sans',
                            fontSize: '16px',
                            fontWeight: 400,
                            textTransform: 'none'
                        })
                    }, children: constants.cookies }), _jsx(Divider, { orientation: "vertical", sx: {
                        border: `1px solid ${textColor}`,
                        height: '20px',
                        mt: '7px !important'
                    }, flexItem: true }), _jsx(Link, { href: ROUTES.TERMS_OF_USE, target: "_blank", rel: "noopener noreferrer", underline: "none", sx: {
                        color: textColor,
                        ...(isNewDesign && {
                            fontFamily: 'Open Sans',
                            fontSize: '16px',
                            fontWeight: 400,
                            textTransform: 'none'
                        })
                    }, children: constants.terms_of_use })] }));
    };
    const getDefaultLoginPage = () => {
        return (_jsx(Container, { disableGutters: true, maxWidth: false, sx: {
                height: '100vh',
                width: '100%',
                maxWidth: '100%',
                maxHeight: '100vh',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: configState?.settings?.background_color || undefined
            }, children: _jsxs(Box, { sx: {
                    display: { xs: 'flex', sm: 'grid' },
                    flexDirection: { xs: 'column', sm: 'unset' },
                    justifyContent: { xs: 'center', sm: 'unset' },
                    alignItems: { xs: 'center', sm: 'unset' },
                    gridTemplateColumns: { sm: '1fr 1fr' },
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden'
                }, children: [_jsx(Box, { sx: {
                            width: '100%',
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: { xs: '100%', md: 'auto' },
                            textAlign: 'center',
                            px: { xs: 2, md: 0 }
                        }, children: _jsxs(Stack, { direction: "column", sx: {
                                width: { xs: '90%', md: '70%' },
                                alignItems: 'center',
                                textAlign: 'center'
                            }, children: [configState?.settings?.show_logo && (_jsx(Box, { component: "img", src: configState?.settings
                                        ?.header_logo_image ?? '', alt: constants.home_header_logo_alt, sx: {
                                        objectFit: 'contain',
                                        height: '50px'
                                    } })), configState?.settings?.show_logo &&
                                    configState?.settings?.include_badge &&
                                    configState?.settings?.industry_name && (_jsx(Box, { sx: {
                                        backgroundColor: '#000000',
                                        color: '#FFFFFF',
                                        borderRadius: '16px',
                                        padding: '4px 16px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: '12px'
                                    }, children: _jsx(Typography, { sx: {
                                            fontFamily: 'Open Sans',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            lineHeight: 1.5,
                                            color: 'inherit'
                                        }, children: `for ${configState.settings.industry_name}` }) })), configState?.settings?.show_workspace_name && (_jsx(Typography, { variant: "h6", sx: {
                                        marginTop: configState?.settings
                                            ?.show_logo
                                            ? '24px'
                                            : '0px',
                                        fontSize: '26px'
                                    }, color: configState?.settings
                                        ?.welcome_message_color, fontWeight: 700, dangerouslySetInnerHTML: {
                                        __html: configState?.settings
                                            ?.workspace_name ?? ''
                                    } })), _jsx(Button, { variant: "contained", size: "large", disabled: loader, sx: {
                                        marginTop: '16px',
                                        textTransform: 'none',
                                        borderColor: `1px solid ${configState?.settings?.button_color} !important`,
                                        backgroundColor: `${configState?.settings?.button_color} !important`,
                                        color: `${configState?.settings?.button_text_color} !important`,
                                        '&:hover': {
                                            backgroundColor: configState?.settings?.button_color,
                                            borderColor: configState?.settings?.button_color,
                                            color: `${configState?.settings?.button_text_color} !important`
                                        },
                                        '&:disabled': {
                                            backgroundColor: `${configState?.settings?.button_color} !important`,
                                            color: `${configState?.settings?.button_text_color} !important`,
                                            opacity: 0.7
                                        }
                                    }, onClick: (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setLoader(true);
                                        RedirectToLogin();
                                    }, "data-testid": "login_button", startIcon: loader ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : null, children: loader ? (_jsx(Typography, { sx: {
                                            color: 'inherit',
                                            fontFamily: 'Open Sans',
                                            fontSize: 15,
                                            fontWeight: 600,
                                            letterSpacing: 0.46
                                        }, children: constants.loading })) : (_jsx(Typography, { variant: "body1", fontWeight: 700, children: constants.Login_button })) }), getCookiePrivacyButtons(true, '#595959')] }) }), configState?.settings?.page_image && (_jsx(Box, { sx: {
                            display: { xs: 'none', sm: 'flex' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                            minWidth: 0
                        }, children: _jsx(Box, { component: "img", src: configState.settings.page_image, alt: constants.home_page_image_alt, sx: {
                                height: '100%',
                                width: '100%',
                                maxHeight: '100vh',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block'
                            } }) }))] }) }));
    };
    return _jsx(_Fragment, { children: getDefaultLoginPage() });
};
export default Home;
